import { Box, Stack, Typography } from '@mui/material';
import React, { type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import UniformInCabinGalleryThumb from './UniformInCabinGalleryThumb';
import { fileNameFromImageUrl, parseUniformImageFileName } from './uniformImageFileName';

const uniformCardMetaRowSx = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 1,
} as const;

const uniformCardMetaLabelSx = { flexShrink: 0, lineHeight: 1.43 } as const;

const uniformCardMetaValueSx = {
    flex: 1,
    minWidth: 0,
    wordBreak: 'break-word',
    textAlign: 'end',
} as const;

export interface UniformGalleryImageCardProps {
    src: string;
    cellWidth: number;
    cellHeight: number;
    onOpenModal: () => void;
    isModalActive: boolean;
}

const UniformGalleryImageCard: React.FC<UniformGalleryImageCardProps> = ({
    src,
    cellWidth,
    cellHeight,
    onOpenModal,
    isModalActive,
}) => {
    const { t } = useTranslation();

    const name = fileNameFromImageUrl(src);
    const parsed = parseUniformImageFileName(name);
    const alt = parsed.kind === 'structured' ? `${parsed.plate}, ${parsed.driverName}` : name;

    return (
        <Box
            component="div"
            role="button"
            tabIndex={0}
            aria-label={String(t('content.uniform.view_image_aria'))}
            aria-haspopup="dialog"
            aria-expanded={isModalActive}
            onClick={onOpenModal}
            onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onOpenModal();
                }
            }}
            sx={{
                width: '100%',
                maxWidth: cellWidth,
                height: cellHeight,
                boxSizing: 'border-box',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
                bgcolor: 'background.paper',
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                '&:focus-visible': {
                    outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: 2,
                },
            }}
        >
            <Box sx={{ flex: '1 1 0', minHeight: 0, minWidth: 0, pointerEvents: 'none' }}>
                <UniformInCabinGalleryThumb src={src} alt={alt} />
            </Box>
            <Stack
                spacing={0.75}
                sx={{
                    flexShrink: 0,
                    flex: '0 1 auto',
                    maxHeight: 112,
                    overflow: 'auto',
                    px: 1,
                    py: 0.75,
                    pointerEvents: 'none',
                }}
            >
                {parsed.kind === 'structured' ? (
                    <>
                        <Box sx={uniformCardMetaRowSx}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                component="span"
                                sx={uniformCardMetaLabelSx}
                            >
                                {t('content.uniform.card_driver')}
                            </Typography>
                            <Typography variant="body2" component="span" sx={uniformCardMetaValueSx}>
                                {parsed.driverName} ({parsed.internalId})
                            </Typography>
                        </Box>
                        <Box sx={uniformCardMetaRowSx}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                component="span"
                                sx={uniformCardMetaLabelSx}
                            >
                                {t('content.uniform.card_plate')}
                            </Typography>
                            <Typography variant="body2" component="span" sx={uniformCardMetaValueSx}>
                                {parsed.plate}
                            </Typography>
                        </Box>
                    </>
                ) : (
                    <Typography
                        variant="caption"
                        component="div"
                        sx={{ wordBreak: 'break-all', color: 'text.secondary' }}
                    >
                        {name}
                    </Typography>
                )}
            </Stack>
        </Box>
    );
};

export default UniformGalleryImageCard;
